import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PreconceptionAssessment } from '@/components/PreconceptionAssessment';
import { PendingCasesIndicator } from '@/components/PendingCasesIndicator';
import { hasUnsavedChanges } from '@/hooks/useUnsavedChangesGuard';
import { useFollowupDueCount } from '@/hooks/useFollowupDueCount';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CalendarClock } from 'lucide-react';

const PreconceptionIndex = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const dueCount = useFollowupDueCount();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  const handleSignOut = () => {
    if (hasUnsavedChanges() &&
        !window.confirm('Έχετε μη αποθηκευμένες αλλαγές. Αν αποσυνδεθείτε τώρα η αξιολόγηση δεν θα υποβληθεί. Συνέχεια;')) {
      return;
    }
    signOut();
  };

  const handleBack = () => {
    if (hasUnsavedChanges() &&
        !window.confirm('Έχετε μη αποθηκευμένες αλλαγές. Αν φύγετε τώρα η αξιολόγηση δεν θα υποβληθεί. Συνέχεια;')) {
      return;
    }
    navigate('/');
  };

  const handleFollowups = () => {
    if (hasUnsavedChanges() &&
        !window.confirm('Έχετε μη αποθηκευμένες αλλαγές. Αν φύγετε τώρα η αξιολόγηση δεν θα υποβληθεί. Συνέχεια;')) {
      return;
    }
    navigate('/followups');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-xl">Loading...</h2>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--paper)' }}>
      <header className="border-b bg-white" style={{ borderTopWidth: '4px', borderTopColor: 'var(--arm-preconception)', borderBottomColor: 'var(--hairline)' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-gray-800 hover:bg-gray-800/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Επιστροφή
            </Button>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--ink)' }}>Preconception Concordance Study</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={handleFollowups} className="gap-2 text-white" style={{ backgroundColor: 'var(--brand)' }}>
              <CalendarClock className="w-4 h-4" />
              Επανεξετάσεις
              {dueCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: 'white', color: 'var(--brand)' }}
                >
                  {dueCount}
                </span>
              )}
            </Button>
            <PendingCasesIndicator arm="preconception" />
            <span className="text-sm text-gray-800">{user.email}</span>
            <Button
              variant="outline"
              onClick={handleSignOut}
              className="border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white"
            >
              Αποσύνδεση
            </Button>
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8">
        <PreconceptionAssessment />
      </main>
    </div>
  );
};

export default PreconceptionIndex;
